import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import LayerBadge from "./LayerBadge";
import { api, fmtUSD } from "../lib/api";
import { Copy, FileCode2, Bot, PackageOpen, ShieldAlert, Undo2 } from "lucide-react";
import { toast } from "sonner";

const copyText = async (text, what) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied to clipboard`);
  } catch {
    toast.error("Copy failed — select & copy manually.");
  }
};

export default function RemediationPackageModal({ open, onClose, opportunityId, title }) {
  const [pkg, setPkg] = useState(null);
  const [tf, setTf] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !opportunityId) return;
    setLoading(true);
    Promise.all([
      api.autofix.pkg(opportunityId),
      api.autofix.terraform(opportunityId),
      api.autofix.prompt(opportunityId),
    ])
      .then(([p, t, pr]) => {
        setPkg(p);
        setTf(t);
        setPrompt(pr.prompt);
      })
      .catch((e) => toast.error("Failed to load package", { description: e?.response?.data?.detail || e.message }))
      .finally(() => setLoading(false));
  }, [open, opportunityId]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-sm border-border max-h-[88vh] overflow-y-auto" data-testid="autofix-package-modal">
        <DialogHeader>
          <DialogTitle className="cs-heading flex items-center gap-2">
            <PackageOpen className="w-5 h-5 text-[#00579e]" /> Remediation Package
          </DialogTitle>
          <DialogDescription>{title || pkg?.title}</DialogDescription>
        </DialogHeader>

        {loading && <div className="py-10 text-center text-muted-foreground">Building package…</div>}

        {!loading && pkg && (
          <Tabs defaultValue="package">
            <TabsList className="bg-[hsl(var(--cs-tint))] border border-border rounded-sm p-1 h-auto">
              <TabsTrigger value="package" data-testid="pkg-tab-package" className="rounded-sm data-[state=active]:bg-[#00579e] data-[state=active]:text-white text-xs px-3 py-1.5">
                Package
              </TabsTrigger>
              <TabsTrigger value="terraform" data-testid="pkg-tab-terraform" className="rounded-sm data-[state=active]:bg-[#00579e] data-[state=active]:text-white text-xs px-3 py-1.5">
                <FileCode2 className="w-3.5 h-3.5 mr-1" /> Terraform
              </TabsTrigger>
              <TabsTrigger value="prompt" data-testid="pkg-tab-prompt" className="rounded-sm data-[state=active]:bg-[#00579e] data-[state=active]:text-white text-xs px-3 py-1.5">
                <Bot className="w-3.5 h-3.5 mr-1" /> AI Prompt
              </TabsTrigger>
            </TabsList>

            <TabsContent value="package" className="mt-4 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="border border-border p-3 rounded-sm">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Savings / mo</div>
                  <div className="cs-kpi-value text-lg text-[#00579e] mt-1">{fmtUSD(pkg.projected_monthly_savings_usd)}</div>
                </div>
                <div className="border border-border p-3 rounded-sm">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">REI Score</div>
                  <div className="cs-kpi-value text-lg mt-1" data-testid="pkg-rei-score">{pkg.rei_score}<span className="text-xs text-muted-foreground">/100</span></div>
                </div>
                <div className="border border-border p-3 rounded-sm">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Layer</div>
                  <div className="mt-1.5"><LayerBadge layer={pkg.layer} showName /></div>
                </div>
                <div className="border border-border p-3 rounded-sm">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Trust Level</div>
                  <div className="cs-mono text-sm font-semibold mt-1 uppercase" data-testid="pkg-trust-level">{pkg.trust?.layer_level}</div>
                </div>
              </div>

              <div className="border border-border p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Proposed Change</div>
                <div className="cs-mono text-[12px] text-foreground">{pkg.proposed_change?.action}</div>
                <p className="text-sm text-foreground mt-1">{pkg.proposed_change?.summary}</p>
              </div>

              <div className={`border p-3 rounded-sm ${pkg.blast_radius?.destructive ? "border-[#ef4444]/50 bg-[#fef2f2]" : "border-border"}`} data-testid="pkg-blast-radius">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">
                  <ShieldAlert className="w-3.5 h-3.5" /> Blast Radius
                  {pkg.blast_radius?.destructive && <span className="text-[#b91c1c] font-semibold">· destructive</span>}
                </div>
                <p className="text-sm text-foreground">{pkg.blast_radius?.description}</p>
              </div>

              <div className="border border-border p-3 rounded-sm" data-testid="pkg-rollback">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">
                  <Undo2 className="w-3.5 h-3.5" /> Rollback Plan
                </div>
                <p className="text-sm text-foreground">{pkg.rollback_plan}</p>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Current State (evidence)</div>
                <pre className="cs-mono text-[11px] bg-[#FAFAFA] border border-border p-3 rounded-sm overflow-x-auto max-h-48 leading-relaxed">
{JSON.stringify(pkg.current_state || {}, null, 2)}
                </pre>
              </div>

              <div className="text-[11px] text-muted-foreground">
                Owner: <span className="text-foreground font-medium">{pkg.team}</span> · <span className="cs-mono">{pkg.owner}</span> · {pkg.trust?.description}
              </div>
            </TabsContent>

            <TabsContent value="terraform" className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="cs-mono text-[12px] text-muted-foreground">{tf?.filename}</div>
                <Button size="sm" variant="outline" onClick={() => copyText(tf?.terraform || "", "Terraform diff")} data-testid="copy-terraform" className="rounded-sm h-7 text-xs">
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
              </div>
              <pre className="cs-mono text-[11px] bg-[#0B0B0F] text-[#E2E8F0] p-4 rounded-sm overflow-x-auto max-h-96 leading-relaxed" data-testid="terraform-code">
                {tf?.terraform}
              </pre>
              {!tf?.has_template && (
                <div className="text-[11px] text-[#92400e] bg-[#fef3c7] border border-[#f59e0b]/40 p-2 rounded-sm">
                  No one-click template for this finding type — the snippet above is guidance for a manual IaC change.
                </div>
              )}
            </TabsContent>

            <TabsContent value="prompt" className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">Paste into Cursor, Claude Code or Windsurf — the agent gets full context to draft the fix PR.</div>
                <Button size="sm" onClick={() => copyText(prompt, "AI prompt")} data-testid="copy-ai-prompt" className="rounded-sm h-7 text-xs bg-[#00579e] hover:bg-[#004580] text-white shrink-0 ml-2">
                  <Copy className="w-3 h-3 mr-1" /> Copy Prompt
                </Button>
              </div>
              <pre className="cs-mono text-[11px] bg-[#FAFAFA] border border-border p-4 rounded-sm overflow-x-auto max-h-96 whitespace-pre-wrap leading-relaxed" data-testid="ai-prompt-text">
                {prompt}
              </pre>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
